import { isInSmallScreen } from "../../core/helpers/screen-util.js";
import AccountingDocumentState from "../shared/documentInfrastructure/AccountingDocumentState.js";
import ForecastConfiguration from "../../models/forecast/template/ForecastConfiguration.js";
import RuleTemplateInheritingCacheManager from "../../models/forecast/template/RuleTemplateInheritingCacheManager.js";

const configuration = {
	forcedIsSmallScreenValue: undefined,
};

/**
 * Hold and manage the state for the ForecastViewerDocument.
 *
 * @param {boolean} [saveSpaceMode] - When true, the view tries to maximize
 *   the number of report rows on screen
 */
export default class ForecastViewerDocumentState extends AccountingDocumentState {
	constructor({ saveSpaceMode = isInSmallScreen({ configuration }) } = {}) {
		super(...arguments);

		this._forecast = undefined;
		this._isInSaveSpaceMode = saveSpaceMode;
		this._scenario = undefined;
		this._vatSettings = undefined;
	}

	getForecast() {
		return this._forecast;
	}

	getForecastName() {
		return this._forecast.getName();
	}

	getForecastDescription() {
		return this._forecast.getDescription();
	}

	isInSaveSpaceMode() {
		return this._isInSaveSpaceMode;
	}

	setSaveSpaceMode(value) {
		this._isInSaveSpaceMode = value;
	}

	getBudgets() {
		return [];
	}

	getVatRates() {
		return this._vatSettings.getVatRates();
	}

	getIncomeStatement() {
		return this._forecast.getIncomeStatement();
	}

	getBalanceSheet() {
		return this._forecast.getBalanceSheet();
	}

	getCashFlow() {
		return this._forecast.getCashFlow();
	}

	getReports() {
		return this._forecast.getReports();
	}

	getCurrentReport() {
		return this._currentReport;
	}

	getFirstReport() {
		return this._forecast.getFirstReport();
	}

	getCurrentPeriod() {
		return this._forecast.getCurrentPeriod();
	}

	getForecastTemplateName() {
		return this._forecast.getForecastTemplateName();
	}

	getReportsAsList() {
		return this._forecast.getReportsAsList();
	}

	getReportFromId(reportId) {
		return this._forecast.getReportFromId(reportId);
	}

	getAllAccounts() {
		return [];
	}

	getConfiguration() {
		return new ForecastConfiguration();
	}

	/**
	 * Remember which of the three reports is current. The parameter
	 * `report` can be falsy to indicate that no report is current;
	 * this happens when a non-report tab is selected (e.g.,
	 * "Description").
	 *
	 * @param {AbstractForecastReport} [report] - The currently-active
	 *   report, if any
	 */
	setCurrentReport(report) {
		this._setCurrentTabParameter(report?.getId());
		this._currentReport = report;
	}

	setCurrentReportFromCurrentTab(currentTab) {
		this._currentReport = this._getReportFromCurrentTabId(currentTab);
	}

	getScenario() {
		return this._scenario;
	}

	isCurrentReportTheIncomeStatement() {
		return this.getCurrentReport()?.isIncomeStatement();
	}

	isCurrentReportTheBalanceSheet() {
		return this.getCurrentReport()?.isBalanceSheet();
	}

	/**
	 * Return existing `RuleTemplate` for a specified cell and a rule model class.
	 *
	 * @param {AbstractRow} row - The vertical position of the cell.
	 * @param {AbstractColumn} column - The horizontal position of the cell.
	 * @param {Class} ruleModelClass - As many `RuleTemplate` can be set on a cell,
	 *   filter by this rule model class
	 */
	getOwnRuleTemplateFor({ row, column, ruleModelClass }) {
		return this._scenario.getOwnRuleTemplateFor({
			rowId: row.getStableId(),
			start: column.getStart(),
			ruleModelClass,
		});
	}

	getManualEntriesFor({ row, column }) {
		return this._scenario.getAllManualEntryRuleTemplatesFor({
			rowId: row.getStableId(),
			start: column.getStart(),
		});
	}

	getManualJournalEntriesFor({ row, column }) {
		return this._scenario.getAllManualJournalEntryRuleTemplatesFor({
			accountNumber: row.getAccount().getNumber(),
			start: column.getStart(),
		});
	}

	/**
	 * Return ruleTemplate inherited by a specified cell and for a rule model class.
	 *
	 * @param {AbstractRow} row - The vertical position of the cell.
	 * @param {AbstractColumn} column - The horizontal position of the cell.
	 * @param {Class} ruleModelClass - As many `RuleTemplate` can be set on a cell,
	 * filter by this rule model class.
	 */
	getInheritedRuleTemplateFor({ row, column, ruleModelClass }) {
		return this._ruleTemplateInheritingCacheManager.getInheritedRuleTemplateFor(
			{
				row,
				column,
				ruleModelClass,
				scenario: this._scenario,
				forecastReport: this._currentReport,
			},
		);
	}

	getRuleTemplateSource() {
		return this._service.getRuleTemplateSource({
			forecastId: this._forecast.getId(),
		});
	}

	getBalanceSheetRowForAccountNumber({ accountNumber }) {
		return this.getBalanceSheet().getRowForAccountNumber(accountNumber);
	}

	getCompany() {
		return this._company;
	}

	getNotificationSettings() {
		return {
			report: this._forecast,
			company: this._company,
			currentUser: this._currentUser,
			companyUsers: this._companyUsers,
			agencyUsers: this._agencyUsers,
			sendNotifications: (emailContent) =>
				this._service.sendNotifications({
					forecastId: this._forecast.getId(),
					emailContent,
				}),
		};
	}

	getRowForAccountNumber(accountNumber) {
		return this._forecast.getRowForAccountNumber(accountNumber);
	}

	canSaveToBudget() {
		return this._canSaveToBudget;
	}

	saveForecastToNewBudget({ budgetName, start, end }) {
		return this._service.saveForecastToNewBudget({
			forecastId: this._forecast.getId(),
			budgetName,
			start,
			end,
		});
	}

	_setupInitialData({
		forecast,
		currentTabId,
		setCurrentTabParameter = () => {},
		canSaveToBudget,
		vatSettings,
		company,
		forecastTemplate,
		currentUser,
		companyUsers,
		agencyUsers,
	}) {
		this._ruleTemplateInheritingCacheManager =
			new RuleTemplateInheritingCacheManager();

		this._forecast = forecast;
		this._canSaveToBudget = canSaveToBudget;
		this._setCurrentTabParameter = setCurrentTabParameter;
		this._currentReport = this.getFirstReport();
		this.setCurrentReportFromCurrentTab(currentTabId);
		this._scenario = forecast.getBaselineScenario();
		this._vatSettings = vatSettings;
		this._company = company;
		this._currentUser = currentUser;
		this._companyUsers = companyUsers;
		this._agencyUsers = agencyUsers;
		this._forecastTemplate = forecastTemplate;
	}

	_getReportFromCurrentTabId(currentTabId) {
		const correspondingReport = this.getReportsAsList().find(
			(report) => report.getId() === currentTabId,
		);

		return correspondingReport || this.getFirstReport();
	}
}
